import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getVmsSettings } from '../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import {
    getVisitorDetails,
    updateVisitor,
} from '../../../redux/actions/SocietyPortal/VisitorAction/VisitorAction';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_VISITORS,
    SOCIETY_PORTAL_VISITORS_VIEW,
} from '../../../Routing/routes';
import VisitorForm from './VisitorForm';
import VisitorNotFound from './components/VisitorNotFound';
import { VisitorLoader } from './components/VisitorListStates';
import { isBlocked } from './visitorConstants';
import { selectedSocietyStorer } from '../../../utils/webStorer';

const EditVisitor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        visitorDetailsData: visitor,
        visitorDetailsLoading,
        updateVisitorLoading,
    } = useSelector(state => state.VisitorReducer || {});
    const settings = useSelector(state => state.VmsReducer?.settings?.data);
    const requireIdProof = settings?.visitor_id_proof_required === true || settings?.visitor_id_proof_required === 1;
    const societyId = selectedSocietyStorer.getSocietyId();

    useEffect(() => {
        if (societyId && !settings) dispatch(getVmsSettings({ societyId }));
    }, [dispatch, societyId, settings]);

    useEffect(() => {
        if (id) dispatch(getVisitorDetails({ id }));
    }, [dispatch, id]);

    const backToProfile = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS_VIEW.replace(':id', id));
    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITORS);

    // Only the editable fields are seeded — visit_count, last_visit_at and the
    // block/delete columns are maintained by the API and rejected on update.
    const initial = useMemo(() => {
        if (!visitor) return null;
        return {
            name: visitor.name || '',
            phone: visitor.phone || '',
            email: visitor.email || '',
            company_name: visitor.company_name || '',
            visitor_type: visitor.visitor_type || 'GUEST',
            id_proof_type: visitor.id_proof_type || '',
            id_proof_number: visitor.id_proof_number || '',
            photo_url: visitor.photo_url || '',
            address: visitor.address || '',
            notes: visitor.notes || '',
        };
    }, [visitor]);

    const handleSubmit = (payload) => {
        dispatch(updateVisitor({ id, ...payload, onSuccess: backToProfile }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-w-full">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Visitors
                </button>

                {visitorDetailsLoading && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-py-10">
                        <VisitorLoader label="Loading visitor..." />
                    </div>
                )}

                {/* The details endpoint 404s for a missing or soft-deleted
                    visitor, so no record after loading means it is gone. */}
                {!visitorDetailsLoading && !visitor && <VisitorNotFound onBack={backToList} />}

                {!visitorDetailsLoading && visitor && (
                    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                        <div className="tw-p-4 tw-border-b tw-border-gray-100">
                            <h1 className="tw-mb-0 tw-text-base tw-font-bold tw-text-gray-800">Edit Visitor</h1>
                            <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">Updating {visitor.name}</p>
                        </div>

                        <div className="tw-p-5">
                            {isBlocked(visitor) && (
                                <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2 tw-mb-4 tw-rounded-md tw-bg-rose-50 tw-border tw-border-rose-100 tw-text-[11px] tw-text-rose-800">
                                    <Icon icon="mdi:block-helper" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                                    <span>
                                        This visitor is currently blocked. Editing their details does not unblock them —
                                        use Unblock on the profile for that.
                                    </span>
                                </div>
                            )}
                            <VisitorForm
                                mode="edit"
                                initial={initial}
                                onSubmit={handleSubmit}
                                onCancel={backToProfile}
                                submitting={updateVisitorLoading}
                                requireIdProof={requireIdProof}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditVisitor;
